<template>
    <section class="section-faq flex flex-col">
        <!--FAQ Bg Image and content div-->
        <div class="faq-bg-div relative flex flex-col phone:h-75">
            <background-image
                :src="sectionFaq.src"
                :srcset="sectionFaq.imgSrcSet" />

            <!--Content and svg icon container-->
            <div
                class="container z-10 flex flex-col items-start flex-grow justify-end  phone:mb-12 tablet:mb-16 desktop:mb-20">
                <!-- FAQ Text with downward arrow-->
                <div class="offset">
                    <!--Content Text Component-->
                    <content-text>
                        <template #title-text>
                            <h2 class="font-bold phone:text-2xl">
                                {{ sectionFaq.title }}
                            </h2>
                        </template>
                        <template #subtitle-text>
                            <h2 class="font-light phone:text-2xl">
                                {{ sectionFaq.subtitle }}
                            </h2>
                        </template>
                    </content-text>

                    <!--SVG Icon-->
                    <svg-icon
                        icon="arrowVerticalLong"
                        class="text-gold-500 h-16" />
                </div>
            </div>
        </div>

        <div class="faq-questions bg-black-700">
            <div class="container flex flex-col tablet:flex-row tablet:flex-wrap content-start">
                <!--FAQ Panels-->
                <div
                    v-for="(faq, index) in faqItems"
                    :key="index"
                    class="panel faq gpu bg-black-700 hover:z-40 hover:shadow-2xl transition-all duration-200 cursor-pointer relative thin-border "
                    :class="{'z-100':showAnswerIndex === index}">
                    <!--Question Panel-->
                    <div
                        class="question panel h-full transition-all duration-200 uppercase text-white tracking-widest relative flex items-center px-8"
                        :class="{'opacity-0':showAnswerIndex === index}"
                        @click="toggleFaq(index)"
                    >
                        <!--Close Panel Icon-->
                        <svg-icon
                            icon="expand"
                            class="expand h-4 w-4 text-gold-500 cursor-pointer absolute top-3 right-3" />
                        <span class="text-sm">{{ faq.title }}</span>
                    </div>

                    <!--Answer Panel-->
                    <transition name="faqanswer">
                        <div
                            v-show="showAnswerIndex === index"
                            class="answer panel gpu transition-all tablet:absolute duration-500 p-6 bg-white py-8 relative shadow-2xl"
                            @click="toggleFaq(index)"
                        >
                            <!--Close Panel Icon-->
                            <svg-icon
                                icon="expand"
                                class="expand h-4 w-4 text-gold-500 cursor-pointer absolute top-3 right-3" />

                            <!--Question Title-->
                            <h4 class="question uppercase mb-4 font-bold pr-3 text-xs tablet:text-3xs text-gray-800">
                                {{ faq.title }}
                            </h4>

                            <!--Answer Text-->
                            <transition name="jump">
                                <div
                                    class="answer text-gray-700 text-xs tablet:text-3xs"
                                    v-html="faq.text" />
                            </transition>
                        </div>
                    </transition>
                </div>

                <!--Fixed Call to Action Panel-->
                <div
                    class="faq gpu bg-black-700 transition-all duration-200 thin-border px-8 hover:z-40 hover:shadow-2xl"
                    @click="toggleFaq('null')">
                    <!--Call Panel-->
                    <div
                        class="question-panel h-full cursor-pointer uppercase text-white tracking-widest flex flex-col justify-center">
                        <span class="text-sm">{{ faqCTA.question }}</span>
                        <tis-button
                            class="btn-flat block text-sm tablet:max-w-xxs group transition-transform"
                            :url="route(faqCTA.url)">
                            {{ faqCTA.buttonLabel }}
                            <svg-icon
                                icon="arrow-horizontal"
                                class="w-6 h-6 inline-block ml-2 transition-transform transform duration-200 group-hover:translate-x-1" />
                        </tis-button>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import TisButton from '@whisper/components/buttons/TisButton';
import SvgIcon from '@whisper/components/icons/SvgIcon';
import BackgroundImage from '@web/components/BackgroundImage';
import ContentText from '@web/components/ContentText';

export default {
  name: 'SectionInstagram',
  components: {
    SvgIcon, TisButton, BackgroundImage, ContentText,
  },
  props: ['sectionFaq', 'faqItems', 'faqCTA'],
  data: () => ({
    showAnswerIndex: null,
  }),
  methods: {
    toggleFaq(index) {
      if (this.showAnswerIndex !== index) {
        this.showAnswerIndex = index;
      } else {
        this.showAnswerIndex = null;
      }
    },
  },
};
</script>

<style scoped lang="scss">
    section.section-faq {
        min-height: 0;
    }

    .thin-border {
        border: 0.5px solid #504F4F;
    }

    .offset  {
        transform: translateX(100px);
    }

    .answer.panel {
        a {
            @apply underline;
        }
    }

    @screen phone {
        .faq {
            height: 154px;
        }
        .answer.panel {
            position: absolute;
            transform-origin: top;
            top: -1rem;
            left: 0;
            z-index: 100;
            transform: scale(1.05)
        }
    }

    @screen tablet {
        .faq {
            flex-basis: 25%;
            height: 240px;
        }

        .faq-bg-div {
            height: 590px;
        }

        .question.panel {
            &:hover {
                transform: scale(1.05);
            }
        }

        .answer.panel {
            position: absolute;
            transform-origin: top;
            top: -2rem;
            left: 0;
            z-index: 100;
            transform: scale(1.65);
        }
        /* All the right hand side panels have to be offset to the left to fit on screen */
        .faq:nth-child(4n) {
            .answer.panel {
                transform: scale(1.65) translateX(-1.75rem);
            }
        }

        /* All the left hand side panels have to be offset to the right to fit on screen */
        .faq:nth-child(4n+1) {
            .answer.panel {
                transform: scale(1.65) translateX(1.75rem);
            }
        }
    }

    @screen tabletLandscape {

        /* All the right hand side panels have to be offset to the left to fit on screen */
        .faq:nth-child(4n) {
            .answer.panel {
                transform: scale(1.65) translateX(-2rem);
            }
        }

        /* All the left hand side panels have to be offset to the right to fit on screen */
        .faq:nth-child(4n+1) {
            .answer.panel {
                transform: scale(1.65) translateX(2rem);
            }
        }
    }

    @screen desktop {

        /* All the right hand side panels have to be offset to the left to fit on screen*/
        .faq:nth-child(4n) {
            .answer.panel {
                transform: scale(1.65) translateX(-2.5rem);
            }
        }

        /* All the left hand side panels have to be offset to the right to fit on screen*/
        .faq:nth-child(4n+1) {
            .answer.panel {
                transform: scale(1.65) translateX(2.5rem);
            }
        }
    }

</style>
